import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { catchError, EMPTY, finalize, tap } from 'rxjs';
import { ValidationErrors } from 'fluentvalidation-ts';

import { createProxy } from "@nstep-common/utils";

import {
	AccountModel,
	AccountService,
	UserLoginModel,
	AuthenticatorStatus,
	AuthService,
	BaseComponent,
	Enviroment,
	JwtTokenModel,
	PasswordSettingsModel,
	ResetPasswordModel,
	ResetPasswordModelValidator,
	SettingsService
} from "@nstep-common/core";

import { flatten } from 'lodash';

@Component({
	selector: 'app-authentication',
	templateUrl: './authentication.component.html',
	styleUrls: ['./authentication.component.css'],
	standalone: false
})
export class AuthenticationComponent extends BaseComponent implements OnInit {
	@Input() userNamePlaceHolder: string = '';
	@Input() passwordPlaceHolder: string = '';
	@Input() userNameMaxLength: number | null = null;
	@Input() passwordMaxLength: number | null = null;

	@Input() onValidation: ((model: UserLoginModel) => ValidationErrors<UserLoginModel>) | null = null;
	@Output() onLoginCompleted = new EventEmitter<JwtTokenModel>();

	loading = false;

	loginValidation: ValidationErrors<UserLoginModel> = {};
	loginInvalid = false;
	loginModel = this.createLoginModel();

	passwordSettings: PasswordSettingsModel = new PasswordSettingsModel();

	resetValidation: ValidationErrors<ResetPasswordModel> = {};
	resetInvalid = false;
	resetModel = this.createResetModel();

	account = new AccountModel();
	authenticator = new AuthenticatorStatus();

	code: string[] = new Array(6).fill('');

	errors: string[] = [];

	askForCode: boolean = false;
	twoFactorSelected: boolean = false;
	email: string = '';
	twoFactorOptions: { key: string, value: string, desc: string | null, icon: string, enabled: boolean }[] = [];

	appVersion = 'N/A';

	constructor(private accountService: AccountService,
		protected authService: AuthService,
		private settingsService: SettingsService,
		private environment: Enviroment) {
		super();

		this.appVersion = `v${this.environment.appVersion}`;
	}

	ngOnInit(): void {
		super.ngOnInit();
		this.logIn();
	}

	private createLoginModel(): UserLoginModel {
		this.loginValidation = {};
		this.loginInvalid = false;

		return createProxy(new UserLoginModel(), {
			set: () => {
				this.errors = [];

				this.resetModel.userName = this.loginModel.userName;
				this.resetModel.oldPassword = this.loginModel.password;

				if (!this.onValidation) {
					return;
				}

				this.loginValidation = this.onValidation(this.loginModel);
				this.loginInvalid = this.hasKeys(this.loginValidation);
			}
		});
	}

	private createResetModel(): ResetPasswordModel {
		this.resetValidation = {};
		this.resetInvalid = false;

		return createProxy(new ResetPasswordModel(), {
			set: () => {
				if (!this.account.requiresPasswordReset) {
					return;
				}

				this.errors = [];

				const validator = new ResetPasswordModelValidator(this.passwordSettings);
				this.resetValidation = validator.validate(this.resetModel);
				this.resetInvalid = this.hasKeys(this.resetValidation);
			}
		});
	}

	private resetState(): void {
		this.loading = false;

		this.loginModel = this.createLoginModel();
		this.resetModel = this.createResetModel();

		this.account = new AccountModel();
		this.authenticator = new AuthenticatorStatus();

		this.code = new Array(6).fill('');

		this.errors = [];
	}

	updateTwoFactorOptions() {
		this.twoFactorOptions = [];

		if (this.authenticator.mobileEnabled) {
			this.twoFactorOptions.push({
				key: '-mobile-',
				value: 'Mobile Authenticator',
				desc: null,
				icon: 'mobile icon',
				enabled: true
			});
		}

		if (this.authenticator.emailEnabled) {
			for (const email of this.authenticator.emails) {
				this.twoFactorOptions.push({
					key: email,
					value: 'Email Authenticator',
					desc: email?.replaceAll('@', '\u200B@'),
					icon: 'envelope icon',
					enabled: true
				});
			}
		}

		if (this.twoFactorOptions.length == 1) {
			this.selectTwoFactor(this.twoFactorOptions[0].key);
		}
	}

	selectTwoFactor(key: string) {
		for (let opt of this.twoFactorOptions) {
			if (opt.key == key) {
				opt.enabled = true;
				this.twoFactorSelected = true;

				if (opt.key == "-mobile-") {
					this.loginModel.email = null;
					this.resetModel.email = null;
				}
				else {
					this.email = opt.key;
					this.resetModel.email = opt.key;
					this.sendEmailCode(opt.key);
				}
			}
			else {
				opt.enabled = false;
			}
		}
	}

	onSubmit(): void {
		if (this.account?.requiresPasswordReset) {
			const validator = new ResetPasswordModelValidator(this.passwordSettings);
			this.resetValidation = validator.validate(this.resetModel);
			this.resetInvalid = this.hasKeys(this.resetValidation);
		}
		else if (this.onValidation) {
			this.loginValidation = this.onValidation(this.loginModel);
			this.loginInvalid = this.hasKeys(this.loginValidation);
		}

		if (!this.account?.requiresPasswordReset ? this.loginInvalid : this.resetInvalid) {
			return;
		}

		this.loading = true;

		this.loginModel.twoFactorCode = this.code.join('');
		this.resetModel.twoFactorCode = this.code.join('');
		this.resetModel.email = this.loginModel.email;

		let observable$ = this.accountService.logIn(this.loginModel);

		if (this.account?.requiresPasswordReset) {
			observable$ = this.accountService.passwordReset(this.resetModel);
		}

		if (this.account.requiresTwoFactorActivation && this.code.join('')) {
			observable$ = this.authenticator.mobileEnabled
				? this.accountService.loginEnableMobileAuthenticator(this.loginModel)
				: this.accountService.loginEnableEmailAuthenticator(this.loginModel, this.email ?? '');
		}

		const submit = observable$
			.pipe(
				tap(response => {
					if (this.account.requiresPasswordReset) {
						this.loginModel.password = this.resetModel.newPassword;
					}

					this.account = response;
					this.authenticator = response.authenticatorStatus ?? new AuthenticatorStatus();

					this.code = new Array(6).fill('');
					this.errors = [];

					if (this.account.requiresPasswordReset) {
						this.subscriptions.push(this.settingsService.getPasswordSettings()
							.subscribe(settings => this.passwordSettings = settings));
					}

					if (this.account.requiresTwoFactor) {
						this.updateTwoFactorOptions();
					}

					if (this.account.requiresTwoFactorActivation) {
						return;
					}

					this.logIn();
				}),
				catchError((resp: any) => {
					const errors = flatten(Object.values(resp)) as string[];
					this.errors = errors;

					return EMPTY;
				}),
				finalize(() => {
					this.loading = false;
				})
			)
			.subscribe()

		this.subscriptions.push(submit);
	}

	logIn(): void {
		if (this.authService.jwt) {
			this.onLoginCompleted.emit(this.authService.jwt);
			return;
		}

		if (!this.account) {
			return;
		}

		const jwt = this.authService.logIn(this.account);

		if (!jwt) {
			return;
		}

		this.onLoginCompleted.emit(jwt);
	}

	confirmAuthenticator(): void {
		if (this.account.requiresTwoFactorActivationDays) {
			return;
		}

		this.code = new Array(6).fill('');
		this.account.requiresTwoFactor = true;

		if (!this.authenticator.emailEnabled) {
			return;
		}

		this.sendEmailCode(this.email!);
	}

	sendEmailCode(email: string): void {
		this.loginModel.email = email;

		const submit = this.accountService.sendEmailAuthenticatorCode(this.loginModel, email)
			.pipe(
				tap(() => {
					this.errors = [];
				}),
				catchError((resp: any) => {
					const errors = flatten(Object.values(resp)) as string[];
					this.errors = errors;

					return EMPTY;
				}),
				finalize(() => {
					this.loading = false;
				})
			)
			.subscribe()

		this.subscriptions.push(submit);
	}

	handleInput(event: any, index: number): void {
		const value = event.target.value;

		if (/^\d$/.test(value)) {
			if (index < 5) {
				document.getElementById(`digit-${index + 1}`)?.focus();
			}
		}
	}

	handleKeyDown(event: any, index: number): void {
		if (event.key === 'Backspace' && !this.code[index] && index > 0) {
			document.getElementById(`digit-${index - 1}`)?.focus();
		}
	}

	handlePaste(event: ClipboardEvent): void {
		event.preventDefault();
		const pasteData = event.clipboardData?.getData('text') || '';
		const digits = pasteData.replace(/\D/g, '').slice(0, 6).split('');

		digits.forEach((digit, i) => {
			if (i < 6) {
				this.code[i] = digit;
				const inputElement = document.getElementById(`digit-${i}`) as HTMLInputElement;
				if (inputElement) {
					inputElement.value = digit;
				}
			}
		});

		const nextEmptyIndex = digits.length < 6 ? digits.length : 5;
		document.getElementById(`digit-${nextEmptyIndex}`)?.focus();
	}
}
